import { supabaseClient } from '../supabase';
import type { TicketmasterConfig, TicketmasterEvent } from './types';
import { ticketmasterEventAdapter } from '../adapters/ticketmasterEventAdapter';
import { logger } from '../logger';
import { HOTEL_ID } from '../supabase/configClient';
import { ticketmasterCache } from './cache';

class TicketmasterClient {
  private static instance: TicketmasterClient;
  private apiKey: string;
  private baseUrl: string;
  private rateLimitDay: number;
  private rateLimitMinute: number;
  private requestCount: { [key: string]: number } = {};
  private retryCount: number = 0;
  private maxRetries: number = 3;
  private retryDelay: number = 1000;

  private constructor() {
    this.apiKey = import.meta.env.VITE_TICKETMASTER_API_KEY;
    this.baseUrl = import.meta.env.VITE_TICKETMASTER_API_URL || 'https://app.ticketmaster.com/discovery/v2';
    this.rateLimitDay = Number(import.meta.env.VITE_TICKETMASTER_RATE_LIMIT_DAY) || 5000;
    this.rateLimitMinute = Number(import.meta.env.VITE_TICKETMASTER_RATE_LIMIT_MINUTE) || 100;

    if (!this.apiKey) {
      throw new Error('Missing Ticketmaster API key');
    }
  }

  public static getInstance(): TicketmasterClient {
    if (!TicketmasterClient.instance) {
      TicketmasterClient.instance = new TicketmasterClient();
    }
    return TicketmasterClient.instance;
  }

  private async checkRateLimit(): Promise<boolean> {
    const now = Date.now();
    const today = new Date().toISOString().split('T')[0];
    const minute = Math.floor(now / 60000);

    // Clean old counters
    Object.keys(this.requestCount).forEach(key => {
      if (!key.startsWith(today) && !key.startsWith(minute.toString())) {
        delete this.requestCount[key];
      }
    });

    // Initialize counters if needed
    if (!this.requestCount[today]) {
      this.requestCount[today] = 0;
    }
    if (!this.requestCount[minute.toString()]) {
      this.requestCount[minute.toString()] = 0;
    }

    // Check limits
    if (this.requestCount[today] >= this.rateLimitDay) {
      logger.warn('Daily rate limit exceeded', {
        category: 'API',
        data: { limit: this.rateLimitDay, count: this.requestCount[today] }
      });
      return false;
    }

    if (this.requestCount[minute.toString()] >= this.rateLimitMinute) {
      logger.warn('Minute rate limit exceeded', {
        category: 'API',
        data: { limit: this.rateLimitMinute, count: this.requestCount[minute.toString()] }
      });
      return false;
    }

    // Increment counters
    this.requestCount[today]++;
    this.requestCount[minute.toString()]++;

    return true;
  }

  private async retryOperation<T>(operation: () => Promise<T>): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * Math.pow(2, this.retryCount - 1)));
        return this.retryOperation(operation);
      }
      throw error;
    } finally {
      this.retryCount = 0;
    }
  }

  public async getEvents(config: TicketmasterConfig): Promise<TicketmasterEvent[]> {
    try {
      if (!config.startDateTime || !config.endDateTime) {
        throw new Error('Start and end dates are required');
      }

      // Check cache first
      const cacheParams = {
        startDateTime: config.startDateTime,
        endDateTime: config.endDateTime,
        radius: config.radius,
        classificationIds: config.classificationIds,
        excludedGenreIds: config.excludedGenreIds,
        locale: config.locale
      };
      
      const cachedEvents = ticketmasterCache.getEvents(cacheParams);
      if (cachedEvents) {
        return cachedEvents;
      }

      // Get hotel coordinates from Supabase
      const { data: hotel, error: hotelError } = await supabaseClient
        .from('hotels')
        .select('latitude,longitude')
        .eq('id', HOTEL_ID)
        .single();

      if (hotelError) {
        throw hotelError;
      }

      if (!hotel.latitude || !hotel.longitude) {
        throw new Error('Hotel coordinates not found');
      }

      const latlong = `${hotel.latitude},${hotel.longitude}`;

      if (!await this.checkRateLimit()) {
        throw new Error('API rate limit exceeded');
      }

      const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          throw new Error(`Invalid date format: ${dateStr}`);
        }
        return date.toISOString().slice(0, 19) + 'Z';
      };

      // Build base parameters
      const params = new URLSearchParams({
        apikey: this.apiKey,
        radius: config.radius.toString(),
        unit: 'km',
        size: config.size.toString(),
        locale: config.locale,
        sort: 'date,asc',
        latlong,
        startDateTime: formatDate(config.startDateTime),
        endDateTime: formatDate(config.endDateTime)
      });

      // Add classifications if specified
      if (config.classificationIds && config.classificationIds.length > 0) {
        params.append('classificationId', config.classificationIds.join(','));
      }

      const url = `${this.baseUrl}/events.json?${params.toString()}`;

      const response = await this.retryOperation(async () => {
        const res = await fetch(url);
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`Ticketmaster API error (${res.status}): ${errorText}`);
        }
        return res;
      });

      const data = await response.json();

      if (!data._embedded?.events) {
        // Cache empty results to avoid repeated API calls
        ticketmasterCache.setEvents(cacheParams, []);
        return [];
      }

      // Set the locale in the adapter
      (ticketmasterEventAdapter as any).setLocale(config.locale || 'fr');

      const events = data._embedded.events
        .filter((event: any) => {
          // Filter excluded genres
          const genreId = event.classifications?.[0]?.genre?.id;
          return !genreId || !config.excludedGenreIds.includes(genreId);
        })
        .map((event: any) => ticketmasterEventAdapter.formatEvent(event))
        .filter((event: TicketmasterEvent | null): event is TicketmasterEvent => event !== null);

      // Check for missing terms in trad_cat_type
      await (ticketmasterEventAdapter as any).checkMissingTermsInTradCatType();

      // Cache the results
      ticketmasterCache.setEvents(cacheParams, events);

      return events;

    } catch (error) {
      logger.error('Error fetching Ticketmaster events', {
        category: 'API',
        data: { 
          error,
          errorDetails: error instanceof Error ? {
            message: error.message,
            stack: error.stack
          } : error,
          config 
        }
      });
      throw error;
    }
  }
}

export const ticketmasterClient = TicketmasterClient.getInstance();